import { ref, computed, onMounted, watch, nextTick } from 'vue'
import deepClone from 'foundation/js/deepClone'
import VueEasyLightbox from 'vue-easy-lightbox'
import Glide from '@glidejs/glide'

let glide

export default {
    name: 'ImageGallery',
    components: { VueEasyLightbox },
    props: {
        imgs: {
            type: Object,
            required: true
        },
        type: {
            type: String,
            required: false,
            default: 'Product'
        }
    },
    setup(props) {
        const imgs = computed(() => props.imgs.imgs)

        const isProduct = computed(() => props.type === 'Product')
        const imgRatio = computed(() => (isProduct.value ? '5-4' : '2-3'))
        const visibleRef = ref(false)
        let indexRef = ref(0) // default 0
        const imgsRef = ref(imgs.value)
        const activeImg = ref(deepClone(imgs.value[0]))
        const lastTriggerEl = ref(null)

        const transformationDesktop = computed(() =>
            isProduct.value
                ? 'transform:fill,width:1000,height:800'
                : 'transform:fill,width:640,height:905'
        )
        const transformationMobile = computed(() =>
            isProduct.value
                ? 'transform:fill,width:400,height:320'
                : 'transform:fill,width:430,height:608'
        )

        const mobileSlider = ref(window.innerWidth > 475 ? false : true)

        const onShow = () => {
            visibleRef.value = true
        }

        const showMultiple = (e) => {
            lastTriggerEl.value = e?.currentTarget || document.activeElement

            imgsRef.value = imgsRef.value

            let isActiveImg = (element) => element.src === activeImg.value.src
            let activeIndex = imgsRef.value.findIndex(isActiveImg)

            indexRef.value = activeIndex

            onShow()
        }

        const onHide = () => (visibleRef.value = false)
        const updateActiveImg = (img) => {
            activeImg.value = { ...img }
        }

        const inertedEls = ref([])

        const getLightboxEl = () =>
            document.querySelector('.vel-modal') ||
            document.querySelector('.vel-overlay') ||
            document.querySelector('.vel') ||
            null

        const getPageRoot = () =>
            document.querySelector('main.kb-page-frame') ||
            document.querySelector('main') ||
            document.querySelector('#app')

        const markInert = (el) => {
            if (!el) return
            if (inertedEls.value.includes(el)) return

            el.setAttribute('inert', '')
            el.setAttribute('aria-hidden', 'true')
            inertedEls.value.push(el)
        }

        const clearInert = () => {
            inertedEls.value.forEach((el) => {
                el.removeAttribute('inert')
                el.removeAttribute('aria-hidden')
            })
            inertedEls.value = []
        }

        const setInertExceptBranch = (keepEl, stopAtEl) => {
            if (!keepEl || !stopAtEl) return

            let current = keepEl
            while (current && current !== stopAtEl) {
                const parent = current.parentElement
                if (!parent) break

                Array.from(parent.children).forEach((sib) => {
                    if (sib === current) return
                    markInert(sib)
                })

                current = parent
            }
        }

        const setPageInert = (open) => {
            const pageRoot = getPageRoot()
            const lightboxEl = getLightboxEl()
            document.body.style.overflow = open ? 'hidden' : ''

            if (!open) {
                clearInert()
                return
            }

            if (!pageRoot) return
            if (!lightboxEl) return

            if (!pageRoot.contains(lightboxEl)) {
                markInert(pageRoot)
                return
            }

            setInertExceptBranch(lightboxEl, pageRoot)
        }

        const focusInsideLightbox = () => {
            const closeBtn = document.querySelector(
                '.kb-image-module__lightbox-controls-close'
            )
            if (closeBtn?.focus) return closeBtn.focus()

            const lb = getLightboxEl()
            const firstFocusable = lb?.querySelector(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            )
            firstFocusable?.focus?.()
        }

        onMounted(() => {
            glide = new Glide('#imageGalleryGlideId', {
                perView: 1.15,
                gap: 15,
                peek: {
                    after: 0,
                    before: 0
                }
            })

            if (imgs.value.length > 1) {
                glide.mount()
            }

            window.addEventListener('resize', () => {
                if (window.innerWidth > 475) {
                    mobileSlider.value = false
                } else if (window.innerWidth <= 475) {
                    mobileSlider.value = true

                    if (imgs.value.length > 1) {
                        glide.mount()
                    }
                }
            })
        })

        const localizeToolbar = async () => {
            await nextTick()
            requestAnimationFrame(() => {
                const map = {
                    'zoom in button': 'Zoom ind',
                    'zoom out button': 'Zoom ud',
                    'resize image button': 'Tilpas til original størrelse',
                    'image rotate left button': 'Drej til venstre',
                    'image rotate right button': 'Drej til højre'
                }

                document
                    .querySelectorAll('.vel-toolbar [aria-label]')
                    .forEach((el) => {
                        const old = el.getAttribute('aria-label')
                        if (map[old]) el.setAttribute('aria-label', map[old])

                        if (!el.hasAttribute('tabindex')) {
                            el.setAttribute('tabindex', '0')
                        }

                        el.addEventListener('keydown', (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                el.click()
                            }
                        })
                    })
            })
        }

        watch(visibleRef, async (open) => {
            if (open) {
                await nextTick()
                localizeToolbar()
                setPageInert(true)
                await nextTick()
                requestAnimationFrame(() => focusInsideLightbox())
            } else {
                setPageInert(false)
                requestAnimationFrame(() => {
                    lastTriggerEl.value?.focus?.()
                })
            }
        })

        return {
            visibleRef,
            indexRef,
            imgsRef,
            showMultiple,
            onHide,
            imgs,
            imgRatio,
            activeImg,
            mobileSlider,
            updateActiveImg,
            transformationDesktop,
            transformationMobile,
            localizeToolbar
        }
    }
}
